import { Grid } from '../core/Grid';
import { Attr, Cell, Rect, TRANSPARENT } from '../core/types';
import { DEFAULT_STYLE, Style, applySgr, parseSgrParams } from './SGR';
import { charWidth } from '../unicode/width';

/**
 * ANSI stream parser implemented as an explicit state machine.
 *
 * Regex-based parsing falls over on real .ans files (split sequences, OSC
 * strings, DCS, stray control bytes), so this walks the stream one code point
 * at a time in the classic ground/escape/CSI/string states and maintains a
 * virtual terminal: cursor, style, scroll-free canvas that grows on demand.
 */

export interface ParseOptions {
  /** Terminal width used for wrapping. Rows may still grow past it if the
   *  stream writes further right without wrapping. */
  width?: number;
  /** Hard safety caps so a malformed file cannot allocate unbounded memory. */
  maxWidth?: number;
  maxHeight?: number;
  /** Treat the SUB byte (0x1A) as end-of-file, as ANSI art files do. */
  stopAtSub?: boolean;
  /**
   * Whether a bare LF also returns to column 0.
   *
   * A real terminal with LNM off moves down only, but files — plain text and
   * a good share of .ans art — use LF as a full newline. Defaulting to true is
   * what makes importing both kinds of file do the obvious thing; set false to
   * reproduce strict terminal behaviour.
   */
  lfResetsColumn?: boolean;
}

export interface ParseResult {
  grid: Grid;
  /** Bounds of the cells actually written, before any cropping. */
  usedBounds: Rect | null;
  warnings: string[];
}

const DEFAULTS = {
  width: 80,
  maxWidth: 1000,
  maxHeight: 5000,
  stopAtSub: true,
  lfResetsColumn: true,
};

type State = 'ground' | 'escape' | 'csi' | 'osc' | 'string' | 'charset';

interface Row {
  cells: Cell[];
}

export class AnsiParser {
  private readonly opts: Required<ParseOptions>;
  private rows: Row[] = [];
  private cursorX = 0;
  private cursorY = 0;
  private savedX = 0;
  private savedY = 0;
  private style: Style = { ...DEFAULT_STYLE };
  private state: State = 'ground';
  private params = '';
  private intermediates = '';
  private stringBuffer = '';
  private maxX = -1;
  private maxY = -1;
  private warnings: string[] = [];
  private wrapPending = false;

  constructor(options: ParseOptions = {}) {
    this.opts = { ...DEFAULTS, ...options };
  }

  parse(input: string): ParseResult {
    let text = input;
    if (this.opts.stopAtSub) {
      const sub = text.indexOf('\u001a');
      if (sub >= 0) {
        if (text.slice(sub + 1, sub + 6) === 'SAUCE') {
          this.warnings.push('SAUCE metadata record found and skipped.');
        }
        text = text.slice(0, sub);
      }
    }

    for (const ch of text) {
      this.step(ch);
    }

    return this.finish();
  }

  private step(ch: string): void {
    switch (this.state) {
      case 'ground':
        this.ground(ch);
        break;
      case 'escape':
        this.escape(ch);
        break;
      case 'csi':
        this.csi(ch);
        break;
      case 'osc':
        this.osc(ch);
        break;
      case 'string':
        this.stringState(ch);
        break;
      case 'charset':
        // ESC ( X and friends: consume the designator and carry on.
        this.state = 'ground';
        break;
    }
  }

  private ground(ch: string): void {
    const code = ch.codePointAt(0) ?? 0;
    if (code === 0x1b) {
      this.state = 'escape';
      this.params = '';
      this.intermediates = '';
      return;
    }
    switch (code) {
      case 0x0a: // LF
        this.cursorY += 1;
        if (this.opts.lfResetsColumn) this.cursorX = 0;
        this.wrapPending = false;
        return;
      case 0x0d: // CR
        this.cursorX = 0;
        this.wrapPending = false;
        return;
      case 0x09: { // HT
        this.cursorX = Math.min(this.opts.maxWidth - 1, (Math.floor(this.cursorX / 8) + 1) * 8);
        this.wrapPending = false;
        return;
      }
      case 0x08: // BS
        this.cursorX = Math.max(0, this.cursorX - 1);
        this.wrapPending = false;
        return;
      case 0x07: // BEL
      case 0x00:
        return;
      default:
        break;
    }
    if (code < 0x20) return; // other C0 controls: ignored
    this.write(ch);
  }

  private escape(ch: string): void {
    switch (ch) {
      case '[':
        this.state = 'csi';
        this.params = '';
        this.intermediates = '';
        return;
      case ']':
        this.state = 'osc';
        this.stringBuffer = '';
        return;
      case 'P': // DCS
      case '^': // PM
      case '_': // APC
        this.state = 'string';
        this.stringBuffer = '';
        return;
      case '(':
      case ')':
      case '*':
      case '+':
        this.state = 'charset';
        return;
      case '7':
        this.savedX = this.cursorX;
        this.savedY = this.cursorY;
        this.state = 'ground';
        return;
      case '8':
        this.cursorX = this.savedX;
        this.cursorY = this.savedY;
        this.state = 'ground';
        return;
      case 'c':
        this.reset();
        this.state = 'ground';
        return;
      case 'D':
        this.cursorY += 1;
        this.state = 'ground';
        return;
      case 'M':
        this.cursorY = Math.max(0, this.cursorY - 1);
        this.state = 'ground';
        return;
      case 'E':
        this.cursorY += 1;
        this.cursorX = 0;
        this.state = 'ground';
        return;
      default:
        this.state = 'ground';
        return;
    }
  }

  private csi(ch: string): void {
    const code = ch.codePointAt(0) ?? 0;
    if (code >= 0x30 && code <= 0x3f) {
      this.params += ch;
      return;
    }
    if (code >= 0x20 && code <= 0x2f) {
      this.intermediates += ch;
      return;
    }
    if (code >= 0x40 && code <= 0x7e) {
      this.dispatchCsi(ch);
      this.state = 'ground';
      return;
    }
    // Anything else aborts the sequence.
    this.state = 'ground';
  }

  private osc(ch: string): void {
    const code = ch.codePointAt(0) ?? 0;
    if (code === 0x07) {
      this.state = 'ground';
      return;
    }
    if (code === 0x1b) {
      // ESC \ terminator; the following backslash is swallowed by escape().
      this.state = 'escape';
      return;
    }
    this.stringBuffer += ch;
  }

  private stringState(ch: string): void {
    const code = ch.codePointAt(0) ?? 0;
    if (code === 0x1b) {
      this.state = 'escape';
      return;
    }
    if (code === 0x07) {
      this.state = 'ground';
      return;
    }
    this.stringBuffer += ch;
  }

  private numericParams(): number[] {
    const raw = this.params.replace(/^[?<>=]/, '');
    if (raw === '') return [];
    return raw.split(';').map((p) => {
      const n = Number.parseInt(p, 10);
      return Number.isNaN(n) ? 0 : n;
    });
  }

  private dispatchCsi(final: string): void {
    const p = this.numericParams();
    const p0 = p[0] ?? 0;
    const arg = (i: number, def: number) => (p[i] === undefined || p[i] === 0 ? def : p[i]);

    switch (final) {
      case 'A':
        this.cursorY = Math.max(0, this.cursorY - arg(0, 1));
        break;
      case 'B':
        this.cursorY += arg(0, 1);
        break;
      case 'C':
        this.cursorX += arg(0, 1);
        break;
      case 'D':
        this.cursorX = Math.max(0, this.cursorX - arg(0, 1));
        break;
      case 'E':
        this.cursorY += arg(0, 1);
        this.cursorX = 0;
        break;
      case 'F':
        this.cursorY = Math.max(0, this.cursorY - arg(0, 1));
        this.cursorX = 0;
        break;
      case 'G':
      case '`':
        this.cursorX = Math.max(0, arg(0, 1) - 1);
        break;
      case 'd':
        this.cursorY = Math.max(0, arg(0, 1) - 1);
        break;
      case 'H':
      case 'f':
        this.cursorY = Math.max(0, arg(0, 1) - 1);
        this.cursorX = Math.max(0, arg(1, 1) - 1);
        break;
      case 'J':
        this.eraseDisplay(p0);
        break;
      case 'K':
        this.eraseLine(p0);
        break;
      case 'm':
        this.style = applySgr(this.style, parseSgrParams(this.params.replace(/^[?<>=]/, '')));
        break;
      case 's':
        this.savedX = this.cursorX;
        this.savedY = this.cursorY;
        break;
      case 'u':
        this.cursorX = this.savedX;
        this.cursorY = this.savedY;
        break;
      case 'X': {
        // ECH: erase N characters in place.
        const n = arg(0, 1);
        for (let i = 0; i < n; i++) this.putCell(this.cursorX + i, this.cursorY, blank(this.style));
        break;
      }
      case '@': {
        // ICH: insert blanks, shifting the rest of the row right.
        const row = this.row(this.cursorY);
        const n = arg(0, 1);
        row.cells.splice(this.cursorX, 0, ...Array.from({ length: n }, () => blank(this.style)));
        break;
      }
      case 'P': {
        // DCH: delete characters, shifting the rest of the row left.
        const row = this.row(this.cursorY);
        row.cells.splice(this.cursorX, arg(0, 1));
        break;
      }
      case 'S':
      case 'T':
      case 'h':
      case 'l':
      case 'r':
      case 'n':
      case 'c':
        // Scrolling / mode / report sequences have no meaning for a static
        // canvas import; recorded once so the UI can surface them.
        this.noteUnsupported(final);
        break;
      default:
        this.noteUnsupported(final);
        break;
    }
    this.wrapPending = false;
  }

  private noteUnsupported(final: string): void {
    const msg = `Unsupported CSI sequence ignored: ESC[${this.params}${final}`;
    if (!this.warnings.includes(msg)) this.warnings.push(msg);
  }

  private reset(): void {
    this.cursorX = 0;
    this.cursorY = 0;
    this.style = { ...DEFAULT_STYLE };
  }

  private row(y: number): Row {
    while (this.rows.length <= y && this.rows.length < this.opts.maxHeight) {
      this.rows.push({ cells: [] });
    }
    const clamped = Math.min(y, this.opts.maxHeight - 1);
    return this.rows[clamped] ?? (this.rows[0] ??= { cells: [] });
  }

  private putCell(x: number, y: number, cell: Cell): void {
    if (x < 0 || y < 0 || x >= this.opts.maxWidth || y >= this.opts.maxHeight) return;
    const row = this.row(y);
    while (row.cells.length <= x) row.cells.push(blank(DEFAULT_STYLE));
    row.cells[x] = cell;
    if (x > this.maxX) this.maxX = x;
    if (y > this.maxY) this.maxY = y;
  }

  private eraseDisplay(mode: number): void {
    if (mode === 2 || mode === 3) {
      this.rows = [];
      this.maxX = -1;
      this.maxY = -1;
      this.cursorX = 0;
      this.cursorY = 0;
      return;
    }
    if (mode === 0) {
      this.eraseLine(0);
      for (let y = this.cursorY + 1; y < this.rows.length; y++) this.rows[y].cells = [];
    } else if (mode === 1) {
      this.eraseLine(1);
      for (let y = 0; y < this.cursorY && y < this.rows.length; y++) this.rows[y].cells = [];
    }
  }

  private eraseLine(mode: number): void {
    const row = this.row(this.cursorY);
    if (mode === 0) {
      row.cells.length = Math.min(row.cells.length, this.cursorX);
    } else if (mode === 1) {
      for (let x = 0; x <= this.cursorX && x < row.cells.length; x++) {
        row.cells[x] = blank(this.style);
      }
    } else {
      row.cells = [];
    }
  }

  private write(ch: string): void {
    const w = charWidth(ch);
    if (w === 0) {
      // Combining mark: append to the cell to the left when there is one.
      const row = this.row(this.cursorY);
      const prev = row.cells[this.cursorX - 1];
      if (prev) row.cells[this.cursorX - 1] = { ...prev, char: prev.char + ch };
      return;
    }
    if (this.wrapPending) {
      this.cursorX = 0;
      this.cursorY += 1;
      this.wrapPending = false;
    }
    this.putCell(this.cursorX, this.cursorY, {
      char: ch,
      fg: this.style.fg,
      bg: this.style.bg,
      attrs: this.style.attrs,
    });
    if (w === 2) {
      // Reserve the trailing half of a wide glyph so nothing overwrites it.
      this.putCell(this.cursorX + 1, this.cursorY, {
        char: '',
        fg: this.style.fg,
        bg: this.style.bg,
        attrs: this.style.attrs,
      });
    }
    this.cursorX += w;
    if (this.cursorX >= this.opts.width) this.wrapPending = true;
  }

  private finish(): ParseResult {
    const width = Math.max(1, this.maxX + 1);
    const height = Math.max(1, this.maxY + 1);
    const grid = new Grid(width, height);
    for (let y = 0; y < Math.min(height, this.rows.length); y++) {
      const cells = this.rows[y].cells;
      for (let x = 0; x < Math.min(width, cells.length); x++) {
        grid.set(x, y, cells[x]);
      }
    }
    return {
      grid,
      usedBounds: this.maxX < 0 ? null : { x: 0, y: 0, width, height },
      warnings: this.warnings,
    };
  }
}

function blank(style: Style): Cell {
  return { char: ' ', fg: TRANSPARENT, bg: style.bg, attrs: Attr.NONE };
}

export function parseAnsi(input: string, options: ParseOptions = {}): ParseResult {
  return new AnsiParser(options).parse(input);
}
